/**
 * Modèle pur de l'ouverture mobile du cockpit.
 *
 * La progression est une fonction de la seule position de défilement : aucune
 * minuterie. La disposition finale est la mise en page elle-même : à `t = 1`,
 * toutes les valeurs sont neutres. Les constantes sont partagées avec
 * `styles/mobile.ts`, qui génère les replis CSS de l'état `t = 0`, d'où le
 * titre et sa note au même pixel avant et après le JS. Seul le titre est mis à l'échelle.
 * La note garde sa taille et suit le bas visuel du titre à son écart de mise en page, avec la même
 * translation `ny` que le sous-titre et le bouton ; seuls ces deux derniers apparaissent en fondu.
 *
 * Après `t = 1` (état neutre), le décollage fait monter la figure vers le cadrage : `takeoffOpacity` y fait
 * apparaître les textes et la synthèse en fondu (`description.md:24`), fonction pure du défilement, de 0 en fin
 * d'ouverture à 1 au collage de `.in` (K2). Le fondu n'entre pas dans `OpeningFrame`.
 *
 * Consommateurs : `styles/mobile.ts` et `scenes/cockpit.opening.ts`.
 */
import { clamp, ease, ph } from "./math";
import { CADRAGE_GAP_TOP } from "./cadrage"; // seuil de K2 ; module sans import : aucun cycle

/** Longueur de la piste sticky en `svh` (entre un et un écran et demi). Réglable ici seulement. */
export const OPENING_SVH = 120;

/**
 * Rapport taille plein écran / taille finale du titre. Avec `TITLE_FIT = 4.85`
 * (`styles/mobile.ts`), le titre final d'un iPhone de 402 px vaut 56,1 px.
 */
export const OPENING_TITLE_K = 1.36;

/** Fin de la phase A (titre, lignes) ; la phase B (montée du bloc, entrée de la figure) suit. */
export const OPENING_SPLIT = 0.45;

/** Noms des variables CSS écrites par la liaison et lues par la feuille ; `co` : opacité des textes et de la synthèse pendant le décollage (porteur `.grid`, O11). */
export const OPENING_VARS = {
  k: "--ov-k",
  ty: "--ov-ty",
  ny: "--ov-ny",
  o: "--ov-o",
  pe: "--ov-pe",
  gy: "--ov-gy",
  fx: "--ov-fx",
  co: "--ov-co",
} as const;

/** Mesures en px. */
export type OpeningMetrics = {
  /** Petit viewport : `L × 100 / OPENING_SVH`. */
  readonly viewH: number;
  /** Hauteur de l'en-tête. */
  readonly hdrH: number;
  /** Haut de mise en page du bloc dans la scène collée (`padding-top` de `.pin`). */
  readonly top: number;
  /** Hauteur de `#hero-block`. */
  readonly blockH: number;
  /** Hauteur de `h1`. */
  readonly titleH: number;
  /** Décalage de mise en page du haut de `.hnote` depuis le haut de `h1`. 0 si non mesuré : le bloc se réduit au titre. */
  readonly noteTop: number;
  /** Hauteur de `.hnote`. 0 si non mesuré : le bloc se réduit au titre. */
  readonly noteH: number;
  /** Largeur du document. */
  readonly viewW: number;
};

/** État d'affichage de l'ouverture. */
export type OpeningFrame = {
  readonly k: number;
  readonly ty: number;
  /** Translation de la note, de la description et du bouton : accrochés sous le bas visuel du titre. */
  readonly ny: number;
  /** Opacités : sous-titre, bouton. */
  readonly o: readonly [number, number];
  readonly ctaLive: boolean;
  readonly gy: number;
  readonly fx: number;
};

/**
 * Progression `[0 ; 1]` de l'ouverture.
 * @param scrolled défilement dans la piste (px)
 * @param length longueur de la piste (px) ; nulle ou négative : séquence terminée
 */
export function openingProgress(scrolled: number, length: number): number {
  return length > 0 ? clamp(scrolled / length) : 1;
}

/**
 * État d'affichage à la progression `t` (hors `[0 ; 1]` : état de la borne).
 * Invariants : à `t = 1`, `k = 1`, `ty = ny = gy = fx = 0`, `o = [1,1]` ;
 * à `t = 0`, `top + gy + ty` vaut le haut visuel du bloc centré sous l'en-tête, de hauteur
 * `unitH + (K0 − 1)·titleH` (seul le titre est agrandi), et la note est à
 * `k·titleH + (noteTop − titleH)` sous le haut visuel du titre.
 */
export function openingFrame(t: number, m: OpeningMetrics): OpeningFrame {
  const a = ph(t, 0, OPENING_SPLIT);
  const b = ph(t, OPENING_SPLIT, 1);
  const eA = ease(a);
  const eB = ease(b);
  const area = m.viewH - m.hdrH;
  const lift = Math.max(0, (area - m.blockH) / 2 - (m.top - m.hdrH)); // décalage du bloc centré
  const k = OPENING_TITLE_K + (1 - OPENING_TITLE_K) * eA;
  const unitH = Math.max(m.titleH, m.noteTop + m.noteH); // bloc de mise en page « titre + note »
  const block0 = unitH + (OPENING_TITLE_K - 1) * m.titleH; // hauteur visuelle à t = 0 : seul le titre est agrandi
  const titleTop0 = m.hdrH + Math.max(0, (area - block0) / 2);
  const ty = (titleTop0 - m.top - lift) * (1 - eA);
  const ny = ty + m.titleH * (k - 1); // note, sous-titre, bouton : sous le bas visuel du titre
  const o = [ease(ph(a, 0.5, 0.9)), ease(ph(a, 0.6, 1))] as const; // sous-titre, bouton
  return { k, ty, ny, o, ctaLive: o[1] > 0.5, gy: lift * (1 - eB), fx: m.viewW * (1 - eB) };
}

/**
 * Distance de décollage, en px de défilement : de la fin de l'ouverture (`.pin` en butée, figure à
 * `top + figOff` du haut de l'écran) au collage de `.in` (figure à `hdrH + CADRAGE_GAP_TOP`, seuil de K2).
 * Nulle ou négative : la figure est déjà au cadrage en fin d'ouverture.
 * @param m mesures de l'ouverture (`top`, `hdrH`)
 * @param figOff `--cad-off` (décalage de mise en page de la figure dans `.in`), 0 si absent : repli de K2
 */
export function takeoffLength(m: Pick<OpeningMetrics, "top" | "hdrH">, figOff: number): number {
  return m.top + figOff - m.hdrH - CADRAGE_GAP_TOP;
}

/**
 * Opacité des textes et de la synthèse : 0 jusqu'à la fin de l'ouverture incluse, 1 à l'arrivée sur le
 * cadrage. Fonction pure du défilement (réversible), courbe `ease` des fondus de l'ouverture sur tout le
 * décollage ; réglage de la courbe ici seulement (R-6e, à juger sur appareil).
 * @param scrolled défilement dans la piste (px), comme `openingProgress`
 * @param length longueur de la piste (px) ; nulle ou négative : séquence terminée, 1
 * @param takeoff distance de décollage (`takeoffLength`) ; nulle ou négative : 1
 */
export function takeoffOpacity(scrolled: number, length: number, takeoff: number): number {
  return length > 0 ? ease(openingProgress(scrolled - length, takeoff)) : 1;
}
